import React from "react";
import styled from "styled-components";
import { vwDesktop, vwMobile } from "../../helpers/styles";
import { BREAKPOINTS } from "../../helpers/theme";
import { getAssetURL } from "../../helpers/assets";

export type TableWithImageSectionProps = {
  isPublished?: boolean;
  title: string;
  background_color?: string;
  text_color?: string;
  image?: {
    id: string;
    filename_disk: string;
  };
  image_alt_text?: string;
  content: string;
};

export default function TableWithImageSection(
  props: TableWithImageSectionProps,
): JSX.Element | null {
  return (
    <Container
      backgroundColor={props?.background_color ?? "#13320C"}
      textColor={props?.text_color ?? "var(--color-white)"}
    >
      <Column>
        <Title>{props?.title}</Title>
        <Content
          dangerouslySetInnerHTML={{
            __html: props.content,
          }}
        />
      </Column>
      {props?.image && (
        <Column>
          <Image
            src={getAssetURL(props?.image)}
            alt={props?.image_alt_text}
          />
        </Column>
      )}
    </Container>
  );
}

const Container = styled.section<{
  backgroundColor?: string;
  textColor?: string;
}>`
  --text-color: ${(props) => props.textColor};
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(27)};
  padding: ${vwMobile(47)} ${vwMobile(30)};
  background-color: ${(props) => props.backgroundColor};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  font-family: ${(props) => props.theme.fontFamily},
    ${(props) => props.theme.fallBackFontFamily}, sans-serif;
  color: var(--text-color);

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    flex-direction: row;
    gap: ${vwDesktop(85)};
    padding: ${vwDesktop(48)} ${vwDesktop(106)};
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${vwMobile(16)};

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    gap: ${vwDesktop(27)};

    &:first-child {
      width: 55%;
    }
    &:last-child {
      width: 45%;
    }
  }
`;

const Title = styled.p`
  font-size: ${vwMobile(40)};
  line-height: 0.92;
  letter-spacing: 0.03em;
  font-weight: 900;
  color: var(--text-color);

  @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
    font-size: ${vwDesktop(59)};
  }
`;

const Content = styled.div`
  table {
    width: 100%;
    border: none;
    tr {
      height: initial !important;
      border-bottom: 3px solid
        color-mix(
          in srgb,
          var(--text-color) 20%,
          transparent
        );
      &:first-child {
        td {
          font-size: ${vwMobile(10)};

          @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
            font-size: ${vwDesktop(12)};
          }
        }
        background-color: transparent;
      }
      &:not(:first-child) {
        td {
          font-weight: 500;
        }
      }
    }
    td {
      padding-block: ${vwMobile(16)};
      padding-inline: ${vwMobile(8)};
      &:first-child {
        padding-left: 0;
        width: fit-content !important;
      }
      &:last-child {
        padding-right: 0;
      }
      &:not(:first-child) {
        width: initial !important;
        text-align: right;
      }
      border: none;
      height: initial !important;
      font-size: ${vwMobile(12)};
      p {
        margin: 0;
      }
      @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
        padding-block: ${vwDesktop(16)};
        padding-inline: ${vwDesktop(8)};
        font-size: ${vwDesktop(12)};
      }
    }
  }

  & > p {
    font-size: ${vwMobile(12)};
    line-height: 1.5;
    letter-spacing: 0.03em;
    font-weight: 400;
    text-align: center;
    margin-top: ${vwMobile(16)};

    @media (min-width: ${BREAKPOINTS.DESKTOP}px) {
      font-size: ${vwDesktop(12)};
      margin-top: ${vwDesktop(16)};
    }
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
`;
